import _ from 'lodash';
import { FEATURE_FLAGS_NAMES, metricNames } from '@linearb/linjs-common';
import {
  isCustomStagesMetric,
  extractDatesFromMetricPoint,
  mapMetricPointToGroupByRedirectParam,
  getRedirectParamsByMetric
} from 'common/constants/metrics';
import useSettingsService from 'components/hooks/useSettingsService';
import get from 'lodash/get';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import { TRACKING } from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';
import { useDispatch, useSelector } from 'react-redux';
import history from 'common/RouterHistory';
import QueryString from 'query-string';
import { pmIntegrationSelector } from 'state/selectors/sensors-authorizations';
import { toggleModal } from 'state/callers';
import { createModalOptions } from 'modals/modal-options-factory';
import MetricsInspection from '../../../MetricsInspection/MetricsInspection';
import MetricsInspectionMFA from '../../../MetricsInspectionMFA';
import {
  parseMetricsFiltersToMetricsInspection,
  validateMetricsInspectionProps
} from '../../../MetricsInspection/state/state.utils';
import { METRICS_WITH_INSPECTION_SUPPORTED } from '../../consts';
import { getAreaSelectionPeriodFromEvent } from '../../custom-report.util';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { isAllTeamsSelected } from 'views/root/views/home/views/settings/views/Users/permissions/Permissions.service';
import { organizationSelector } from 'state/selectors/organization';
import { toggleSnackBar } from 'state/actions';
import Text from 'components/base/Text';
import { repositoriesSelector } from 'state/selectors/repositories';
import { getServicesSelector } from 'state/selectors/services';
import { isTeam } from 'common/utils/utils';
import useMetricsCalculationConfig from 'views/root/views/home/hooks/useMetricsCalculationConfig';

const isMetricsInspectionEnabledSupported = (
  metricName,
  isCycleTimeInspectionEnabled
) => {
  // special handling for cycle time is temporary
  if (metricName === metricNames.CYCLE_TIME && isCycleTimeInspectionEnabled) {
    return true;
  }
  if (isCustomStagesMetric(metricName)) {
    return true;
  }

  return METRICS_WITH_INSPECTION_SUPPORTED.includes(metricName);
};

const useCustomMetricPointContext = ({
  filters,
  selectedTeam,
  selectTeamId,
  metrics,
  graph,
  metricName,
  editMode,
  isPublic = true
}) => {
  const { getSettingOrDefinitionValue } = useSettingsService();
  const dispatch = useDispatch();
  const pmSensor = useSelector(pmIntegrationSelector);
  const location = useLocation();
  const repositories = useSelector(repositoriesSelector);
  const services = useSelector(getServicesSelector);
  const organization = useSelector(organizationSelector);
  const { metricsCalculationStrategy } = useMetricsCalculationConfig();

  const isQualityMetricsFlowFF = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.qualityMetricsFlow
  });
  const isActivityReleasesEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isActivityReleasesEnabled
  });

  const isCycleTimeInspectionEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isCycleTimeInspectionEnabled
  });

  const isTeamGitSettingEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isTeamGitSettingEnabled
  });

  const isMetricsInspectionMfaEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.metricsInspectionMfaEnabled
  });

  const isMultiCriteriaFiltering =
    isTeamGitSettingEnabled &&
    filters.globallySelectedTeams?.length === 1 &&
    isTeam(filters.globallySelectedTeams?.[0]);

  const isAreaSelectEnabled =
    !editMode &&
    isMetricsInspectionEnabledSupported(
      metricName,
      isCycleTimeInspectionEnabled
    ) &&
    validateMetricsInspectionProps(filters, false, isMultiCriteriaFiltering);

  const isPointClickMetricsInspectionEnabled =
    !editMode &&
    isMetricsInspectionEnabledSupported(
      metricName,
      isCycleTimeInspectionEnabled
    ) &&
    validateMetricsInspectionProps(filters, true, isMultiCriteriaFiltering);

  const getRedirectParams = getRedirectParamsByMetric(
    isQualityMetricsFlowFF,
    isActivityReleasesEnabled
  )({ metricName });

  const toggleMetricsInspectionModal = ({ period = {}, graph, filters }) => {
    dispatch(
      toggleModal(
        createModalOptions({
          height: '90%',
          width: '70%',
          showCloseButton: isMetricsInspectionMfaEnabled,
          content: () =>
            isMetricsInspectionMfaEnabled ? (
              <MetricsInspectionMFA
                dateRange={{ from: period.after, to: period.before }}
                filters={filters}
                isPublic={isPublic}
                metricsCalculationStrategy={metricsCalculationStrategy}
                metricName={metricName}
              />
            ) : (
              <MetricsInspection
                widget={graph}
                period={period}
                filters={filters}
                isPublic={isPublic}
              />
            )
        })
      )
    );
  };
  const toggleBadOrganizationIdSnackbar = () => {
    dispatch(
      toggleSnackBar({
        message: (
          <Text size='large' color='white' align='center'>
            This metric inspection link was created for a diferent organization.
            <br />
            Change the organization you are viewing in your account settings.
          </Text>
        ),
        variant: 'error'
      })
    );
  };
  useEffect(() => {
    const search = QueryString.parse(location.search, {
      ignoreQueryPrefix: true
    });
    if (
      search.inspectingWidgetId === graph?.id?.toString() &&
      search.selectedTimeRanges &&
      search.organizationId
    ) {
      const [after, before] = search.selectedTimeRanges.split(',');
      if (after && before) {
        const paramsOverride = {
          label: filters.selectedLabels?.length && filters.selectedLabels[0],
          team:
            !isAllTeamsSelected(filters.globallySelectedTeams) &&
            filters.globallySelectedTeams[0]?.id
        };
        if (Number(search.organizationId) === Number(organization.id)) {
          onPointClick(graph, { category: { after, before } }, paramsOverride);
        } else {
          toggleBadOrganizationIdSnackbar();
        }
        history.replace({
          pathname: location.pathname,
          search: `?${QueryString.stringify(
            _.omit(search, ['inspectingWidgetId', 'organizationId'])
          )}`
        });
      }
    }
  }, []);

  const onPointClick = (graph, point, paramsOverride = {}) => {
    const { name = '' } = graph;

    const redirectParams = getRedirectParams?.(point);

    if (!redirectParams) {
      return;
    }

    const {
      selectedRepo = [],
      selectedContributor,
      selectedTeams,
      selectedService = []
    } = filters;
    const groupByRedirectParams = mapMetricPointToGroupByRedirectParam(point);

    const clickedTeamId = get(
      groupByRedirectParams,
      'teams',
      paramsOverride.team
    );

    const qsParams = {
      ...redirectParams.qs,
      repositories: selectedRepo?.length ? selectedRepo.join(',') : null,
      author: get(selectedContributor, 'id', null),
      contributors: get(selectedContributor, 'id', null),
      teams:
        get(selectedTeams, '[0].id', null) ||
        get(filters.globallySelectedTeams, '[0].id', null),
      services: selectedService?.length ? selectedService.join(',') : null,
      ...groupByRedirectParams
    };

    const qs = omitBy(qsParams, isNil); // remove all undefined/null props

    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.METRICS,
      click_type: TRACKING.CLICK.DRILLDOWN,
      element: name
    });

    if (isPointClickMetricsInspectionEnabled) {
      const label = get(groupByRedirectParams, 'labels', paramsOverride.label);
      const metricsInspectionFilters = parseMetricsFiltersToMetricsInspection(
        {
          ...filters,
          teamId: clickedTeamId,
          selectedLabels: label ? [label] : [],
          selectedRepo: selectedRepo?.length
            ? repositories.filter(repo => selectedRepo.includes(repo.id))
            : [],
          selectedService: selectedService?.length
            ? services.filter(service => selectedService.includes(service.id))
            : []
        },
        metricName,
        isMultiCriteriaFiltering
      );
      const { to: before, from: after } = extractDatesFromMetricPoint(point);

      return toggleMetricsInspectionModal({
        period: { before, after },
        graph,
        filters: metricsInspectionFilters,
        isPublic
      });
    }

    history.push({
      pathname: redirectParams.path,
      search: QueryString.stringify(qs),
      state: get(redirectParams, 'state', {})
    });
  };

  const onCustomPMDashboardPointClicked = ({
    point,
    pmSensor,
    metrics,
    graphId
  }) => {
    const { index } = point;
    const relevantMetrics = metrics.find(({ group }) => group === graphId);
    const clickedMetric = get(relevantMetrics, `data.[${index}]`);
    const { issues } = clickedMetric;
    if (get(pmSensor, 'extra_data.sensor_url')) {
      const url = `${
        pmSensor.extra_data.sensor_url
      }/issues/?jql=issue in (${issues.join(',')}) ORDER BY updated DESC`;
      window.open(url, '_blank').focus();
    }
  };

  const onAreaSelect = e => {
    e.preventDefault();
    const period = getAreaSelectionPeriodFromEvent(e);

    if (!period) return;

    const { selectedRepo = [], selectedService = [] } = filters;

    const metricsInspectionFilters = parseMetricsFiltersToMetricsInspection(
      {
        ...filters,
        selectedRepo: selectedRepo?.length
          ? repositories.filter(repo => selectedRepo.includes(repo.id))
          : [],
        selectedService: selectedService?.length
          ? services.filter(service => selectedService.includes(service.id))
          : []
      },
      metricName,
      isMultiCriteriaFiltering
    );

    toggleMetricsInspectionModal({
      period,
      graph,
      filters: metricsInspectionFilters
    });
  };

  return {
    default: getRedirectParams
      ? ({ point }) => onPointClick(graph, point)
      : null,
    pm: ({ point }) =>
      onCustomPMDashboardPointClicked({
        point,
        pmSensor,
        metrics,
        graphId: graph?.id
      }),
    ...(isAreaSelectEnabled && { onAreaSelect })
  };
};

export default useCustomMetricPointContext;
